import { db } from '../firebase';
import {
    collection,
    query,
    where,
    onSnapshot,
    doc,
    updateDoc,
    getDoc,
    getDocs,
    runTransaction
} from 'firebase/firestore';
import { sendBookingAccepted, sendBookingRejected, sendWorkCompleted, sendGenericNotification } from './emailService';

/**
 * Booking Service for Service Provider (fixit2) App
 * 
 * Handles fetching, accepting, and rejecting bookings.
 * Providers see real-time booking requests from customers.
 * 
 * Status lifecycle: pending → accepted → in-progress → completed
 *                   pending → rejected
 */

/**
 * Normalize a service category to lowercase-slug format.
 * e.g. 'Electrician' → 'electrician', 'AC Repair' → 'ac-repair'
 */
function normalizeCategory(cat) {
    if (!cat) return '';
    return cat.toLowerCase().replace(/\s+/g, '-');
}

async function resolveCustomerEmail(bookingData) {
    const directEmail = (bookingData?.customerEmail || bookingData?.email || '').trim();
    if (directEmail) return directEmail;

    const customerId = bookingData?.customerId || bookingData?.clientId;
    if (!customerId || String(customerId).startsWith('guest-')) {
        return '';
    }

    try {
        const userSnap = await getDoc(doc(db, 'users', customerId));
        if (!userSnap.exists()) return '';

        const userData = userSnap.data() || {};
        return (userData.email || userData.customerEmail || '').trim();
    } catch (error) {
        console.warn('Could not resolve customer email from users profile:', error);
        return '';
    }
}

async function resolveProviderEmail(bookingData) {
    const directEmail = (bookingData?.providerEmail || '').trim();
    if (directEmail) return directEmail;

    const providerId = bookingData?.providerId;
    if (!providerId) return '';

    try {
        const providerSnap = await getDoc(doc(db, 'serviceProviders', providerId));
        if (!providerSnap.exists()) return '';

        const providerData = providerSnap.data() || {};
        return (providerData.email || '').trim();
    } catch (error) {
        console.warn('Could not resolve provider email from serviceProviders:', error);
        return '';
    }
}

async function triggerEmail(task) {
    try {
        await task();
    } catch (emailError) {
        console.error('Email trigger failed:', emailError);
    }
}

function getFriendlyBookingError(error, fallback = 'Booking action failed. Please try again.') {
    const code = error?.code || '';
    const message = (error?.message || '').toLowerCase();

    if (code === 'permission-denied' || message.includes('permission')) {
        return 'Permission denied. Please login again and retry.';
    }
    if (code === 'unavailable' || message.includes('network')) {
        return 'Network issue detected. Please check your internet and retry.';
    }
    if (message.includes('already accepted') || message.includes('already')) {
        return error.message;
    }
    return error?.message || fallback;
}

// ─── FETCH BOOKINGS ─────────────────────────────────────────────────────────

/**
 * Get all bookings assigned to a specific provider (one-time fetch).
 */
export async function getProviderBookings(providerId) {
    try {
        const q = query(
            collection(db, 'bookings'),
            where('providerId', '==', providerId)
        );
        const snapshot = await getDocs(q);
        const results = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        results.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        return results;
    } catch (error) {
        console.error('❌ Failed to fetch provider bookings:', error);
        throw error;
    }
}

/**
 * Get all PENDING bookings matching a provider's service category.
 * These are open requests that any matching provider can accept.
 */
export async function getAvailableBookings(serviceCategory) {
    try {
        // Fetch all pending bookings, then filter by category client-side
        const q = query(
            collection(db, 'bookings'),
            where('status', '==', 'pending')
        );
        const snapshot = await getDocs(q);
        const normalized = normalizeCategory(serviceCategory);
        const results = snapshot.docs
            .map(d => ({ id: d.id, ...d.data() }))
            .filter(b => {
                const cat = normalizeCategory(b.serviceCategory);
                return cat === normalized;
            });
        results.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        return results;
    } catch (error) {
        console.error('❌ Failed to fetch available bookings:', error);
        throw error;
    }
}

/**
 * Get ALL bookings for a provider — both assigned to them and
 * open requests in their service category.
 */
export async function getAllRelevantBookings(providerId, serviceCategory) {
    try {
        // Get bookings assigned to this provider
        const assigned = await getProviderBookings(providerId);

        // Get open pending bookings in the same category
        const open = await getAvailableBookings(serviceCategory);

        // Merge and deduplicate
        const seen = new Set(assigned.map(b => b.id));
        const merged = [...assigned];
        for (const booking of open) {
            if (!seen.has(booking.id)) {
                merged.push(booking);
            }
        }

        // Sort by createdAt DESC
        merged.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        return merged;
    } catch (error) {
        console.error('❌ Failed to fetch all relevant bookings:', error);
        throw error;
    }
}

// ─── REAL-TIME LISTENERS ────────────────────────────────────────────────────

/**
 * Listen to bookings relevant to a provider in real-time.
 * Combines: (1) bookings assigned to this provider, (2) open pending bookings in their category.
 * Returns an unsubscribe function.
 * 
 * Uses simple single-field queries to avoid needing Firestore composite indexes.
 */
export function listenToProviderBookings(providerId, serviceCategory, callback) {
    const assignedMap = new Map();
    const pendingMap = new Map();
    let assignedReady = false;
    let pendingReady = false;
    const normalized = normalizeCategory(serviceCategory);

    function emitMerged() {
        if (!assignedReady || !pendingReady) return;

        const merged = new Map();

        // Add all assigned bookings
        for (const [id, booking] of assignedMap) {
            merged.set(id, booking);
        }

        // Add pending bookings that match the service category
        // Only include bookings that are OPEN (no providerId) or assigned to this provider
        for (const [id, booking] of pendingMap) {
            if (!merged.has(id)) {
                const cat = normalizeCategory(booking.serviceCategory);
                const isOpen = !booking.providerId; // flexible: no assigned provider
                const isAssignedToMe = booking.providerId === providerId; // immediate: assigned to me
                if (cat === normalized && (isOpen || isAssignedToMe)) {
                    merged.set(id, booking);
                }
            }
        }

        const all = Array.from(merged.values());
        all.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        callback(all);
    }

    // Listener 1: Bookings assigned to this provider (simple single-field query)
    const assignedQuery = query(
        collection(db, 'bookings'),
        where('providerId', '==', providerId)
    );

    const unsub1 = onSnapshot(assignedQuery, (snapshot) => {
        assignedMap.clear();
        snapshot.docs.forEach(d => {
            assignedMap.set(d.id, { id: d.id, ...d.data() });
        });
        assignedReady = true;
        emitMerged();
    }, (error) => {
        console.error('❌ Assigned bookings listener error:', error);
        assignedReady = true;
        emitMerged();
    });

    // Listener 2: All pending bookings (simple single-field query, filtered client-side by category)
    const pendingQuery = query(
        collection(db, 'bookings'),
        where('status', '==', 'pending')
    );

    const unsub2 = onSnapshot(pendingQuery, (snapshot) => {
        pendingMap.clear();
        snapshot.docs.forEach(d => {
            pendingMap.set(d.id, { id: d.id, ...d.data() });
        });
        pendingReady = true;
        emitMerged();
    }, (error) => {
        console.error('❌ Pending bookings listener error:', error);
        pendingReady = true;
        emitMerged();
    });

    // Return combined unsubscribe
    return () => {
        unsub1();
        unsub2();
    };
}

// ─── ACCEPT / REJECT / STATUS UPDATES ───────────────────────────────────────

/**
 * Accept a booking request (provider action).
 */
export async function acceptBooking(bookingId, providerId, providerName, providerEmailOverride = '') {
    try {
        const bookingRef = doc(db, 'bookings', bookingId);
        if (!providerId) {
            throw new Error('Please login again before accepting bookings.');
        }

        const now = new Date().toISOString();

        const currentData = await runTransaction(db, async (transaction) => {
            const bookingSnap = await transaction.get(bookingRef);
            if (!bookingSnap.exists()) {
                throw new Error('Booking not found');
            }

            const latest = bookingSnap.data();

            // Prevent accepting if already accepted/updated by someone else.
            if (latest.status !== 'pending') {
                throw new Error(`Cannot accept: booking is already ${latest.status}`);
            }

            const statusHistory = latest.statusHistory || [];

            transaction.update(bookingRef, {
                status: 'accepted',
                providerId,
                providerName,
                updatedAt: now,
                statusHistory: [
                    ...statusHistory,
                    {
                        status: 'accepted',
                        timestamp: now,
                        note: `Accepted by ${providerName}`
                    }
                ]
            });

            return latest;
        });

        console.log('✅ Booking accepted:', bookingId);

        // ── Step 2: Fetch provider pricing and patch onto booking (non-blocking) ──
        void (async () => {
            try {
                const providerRef = doc(db, 'serviceProviders', providerId);
                const providerSnap = await getDoc(providerRef);
                if (providerSnap.exists()) {
                    const providerData = providerSnap.data();
                    const pricingPatch = {};
                    if (providerData.visitFee) pricingPatch.visitFee = providerData.visitFee;
                    if (providerData.hourlyRate) pricingPatch.hourlyRate = providerData.hourlyRate;
                    if (Object.keys(pricingPatch).length > 0) {
                        await updateDoc(bookingRef, pricingPatch);
                    }
                }
            } catch (pricingErr) {
                console.warn('Non-fatal: could not patch provider pricing after accept:', pricingErr);
            }
        })();

        // ── Step 3: Send emails (fully non-blocking, never surfaces as user error) ──
        void (async () => {
            try {
                const customerEmail = await resolveCustomerEmail(currentData);
                if (customerEmail) {
                    await triggerEmail(() => sendBookingAccepted(customerEmail, {
                        customerName: currentData.customerName,
                        providerName,
                        serviceName: currentData.serviceName || currentData.serviceCategory || 'Service',
                        bookingId,
                    }));
                } else {
                    console.warn(`Skipping booking accepted email for ${bookingId}: no customer email found`);
                }
            } catch (emailErr) {
                console.warn('Non-fatal: customer email failed after accept:', emailErr);
            }

            try {
                const providerEmail = (providerEmailOverride || '').trim() || await resolveProviderEmail({ ...currentData, providerId });
                if (providerEmail) {
                    await triggerEmail(() => sendGenericNotification(providerEmail, {
                        title: 'Booking accepted successfully',
                        message: `You accepted booking ${bookingId} for ${currentData.customerName || 'a customer'} (${currentData.serviceName || currentData.serviceCategory || 'Service'}).`,
                    }));
                }
            } catch (emailErr) {
                console.warn('Non-fatal: provider email failed after accept:', emailErr);
            }
        })();

    } catch (error) {
        console.error('❌ Failed to accept booking:', error.code || error.message, error);
        throw new Error(getFriendlyBookingError(error, 'Failed to accept booking.'));
    }
}

/**
 * Reject a booking request (provider action).
 */
export async function rejectBooking(bookingId, providerName, reason = '') {
    try {
        const bookingRef = doc(db, 'bookings', bookingId);
        const bookingSnap = await getDoc(bookingRef);

        if (!bookingSnap.exists()) {
            throw new Error('Booking not found');
        }

        const currentData = bookingSnap.data();
        const statusHistory = currentData.statusHistory || [];

        await updateDoc(bookingRef, {
            status: 'rejected',
            updatedAt: new Date().toISOString(),
            statusHistory: [
                ...statusHistory,
                {
                    status: 'rejected',
                    timestamp: new Date().toISOString(),
                    note: `Rejected by ${providerName}${reason ? ': ' + reason : ''}`
                }
            ]
        });

        const customerEmail = await resolveCustomerEmail(currentData);
        if (customerEmail) {
            void triggerEmail(() => sendBookingRejected(customerEmail, {
                customerName: currentData.customerName,
                serviceName: currentData.serviceName || currentData.serviceCategory || 'Service',
                bookingId,
                reason,
            }));
        } else {
            console.warn(`Skipping booking rejected email for ${bookingId}: no customer email found`);
        }

        console.log('✅ Booking rejected:', bookingId);
    } catch (error) {
        console.error('❌ Failed to reject booking:', error.code || error.message, error);
        throw error;
    }
}

/**
 * Mark a booking as in-progress (provider starts work).
 * Records the first work session start time.
 */
export async function startWork(bookingId, providerName) {
    try {
        const bookingRef = doc(db, 'bookings', bookingId);
        const bookingSnap = await getDoc(bookingRef);

        if (!bookingSnap.exists()) {
            throw new Error('Booking not found');
        }

        const currentData = bookingSnap.data();
        const statusHistory = currentData.statusHistory || [];
        const workSessions = currentData.workSessions || [];
        const now = new Date().toISOString();

        // Start a new work session
        workSessions.push({ start: now, end: null });

        await updateDoc(bookingRef, {
            status: 'in-progress',
            workSessions,
            updatedAt: now,
            statusHistory: [
                ...statusHistory,
                {
                    status: 'in-progress',
                    timestamp: now,
                    note: `Work started by ${providerName}`
                }
            ]
        });

        console.log('✅ Booking marked in-progress:', bookingId);
    } catch (error) {
        console.error('❌ Failed to start work:', error);
        throw error;
    }
}

/**
 * Pause work at end of day (multi-day jobs).
 * Closes the current work session and sets status to work-paused.
 */
export async function pauseWork(bookingId, providerName) {
    try {
        const bookingRef = doc(db, 'bookings', bookingId);
        const bookingSnap = await getDoc(bookingRef);

        if (!bookingSnap.exists()) {
            throw new Error('Booking not found');
        }

        const currentData = bookingSnap.data();
        const statusHistory = currentData.statusHistory || [];
        const workSessions = [...(currentData.workSessions || [])];
        const now = new Date().toISOString();

        // Close the last open session
        const lastSession = workSessions[workSessions.length - 1];
        if (lastSession && !lastSession.end) {
            lastSession.end = now;
        }

        const { totalHours, totalCost } = calculatePricing(workSessions, currentData.visitFee, currentData.hourlyRate);

        await updateDoc(bookingRef, {
            status: 'work-paused',
            workSessions,
            totalHoursWorked: totalHours,
            calculatedTotal: totalCost,
            updatedAt: now,
            statusHistory: [
                ...statusHistory,
                {
                    status: 'work-paused',
                    timestamp: now,
                    note: `Work paused by ${providerName} (${totalHours.toFixed(1)} hrs so far)`
                }
            ]
        });

        console.log('✅ Work paused:', bookingId);
    } catch (error) {
        console.error('❌ Failed to pause work:', error);
        throw error;
    }
}

/**
 * Resume work on a paused booking (next day continuation).
 * Opens a new work session and sets status back to in-progress.
 */
export async function resumeWork(bookingId, providerName) {
    try {
        const bookingRef = doc(db, 'bookings', bookingId);
        const bookingSnap = await getDoc(bookingRef);

        if (!bookingSnap.exists()) {
            throw new Error('Booking not found');
        }

        const currentData = bookingSnap.data();
        const statusHistory = currentData.statusHistory || [];
        const workSessions = [...(currentData.workSessions || [])];
        const now = new Date().toISOString();

        // Open a new session
        workSessions.push({ start: now, end: null });

        await updateDoc(bookingRef, {
            status: 'in-progress',
            workSessions,
            updatedAt: now,
            statusHistory: [
                ...statusHistory,
                {
                    status: 'in-progress',
                    timestamp: now,
                    note: `Work resumed by ${providerName} (Day ${workSessions.length})`
                }
            ]
        });

        console.log('✅ Work resumed:', bookingId);
    } catch (error) {
        console.error('❌ Failed to resume work:', error);
        throw error;
    }
}

// ─── PRICING CALCULATION ────────────────────────────────────────────────────

const OVERTIME_THRESHOLD_HRS = 8; // hours per day before overtime
const OVERTIME_MULTIPLIER = 1.5;

/**
 * Calculate total hours and cost from work sessions.
 * 
 * Logic:
 * - Visit fee covers the 1st hour
 * - Additional hours billed at hourlyRate
 * - If a single day exceeds 8 hours, overtime kicks in at 1.5× hourlyRate
 * 
 * Returns { totalHours, totalCost, breakdown }
 */
export function calculatePricing(workSessions = [], visitFee = 0, hourlyRate = 0) {
    if (!workSessions.length) return { totalHours: 0, totalCost: visitFee, breakdown: [] };

    // Group sessions by day
    const dayMap = {};
    workSessions.forEach(session => {
        const start = new Date(session.start);
        const end = session.end ? new Date(session.end) : new Date();
        const hours = Math.max(0, (end - start) / (1000 * 60 * 60));
        const dayKey = start.toISOString().slice(0, 10); // YYYY-MM-DD

        if (!dayMap[dayKey]) dayMap[dayKey] = { normalHours: 0, overtimeHours: 0, totalHours: 0 };
        dayMap[dayKey].totalHours += hours;
    });

    // Calculate per-day normal vs overtime
    let grandTotalHours = 0;
    let totalNormalHours = 0;
    let totalOvertimeHours = 0;
    const breakdown = [];

    Object.entries(dayMap).forEach(([day, data]) => {
        const normal = Math.min(data.totalHours, OVERTIME_THRESHOLD_HRS);
        const overtime = Math.max(0, data.totalHours - OVERTIME_THRESHOLD_HRS);
        dayMap[day].normalHours = normal;
        dayMap[day].overtimeHours = overtime;
        grandTotalHours += data.totalHours;
        totalNormalHours += normal;
        totalOvertimeHours += overtime;
        breakdown.push({ day, totalHours: data.totalHours, normalHours: normal, overtimeHours: overtime });
    });

    // Billing: visit fee covers 1st hour, then hourly rate
    const billableHours = Math.max(0, grandTotalHours - 1); // 1st hour included in visit fee
    const billableNormal = Math.max(0, totalNormalHours - 1);
    const normalCost = billableNormal * hourlyRate;
    const overtimeCost = totalOvertimeHours * hourlyRate * OVERTIME_MULTIPLIER;
    const totalCost = visitFee + normalCost + overtimeCost;

    return {
        totalHours: Math.round(grandTotalHours * 100) / 100,
        billableHours: Math.round(billableHours * 100) / 100,
        totalCost: Math.round(totalCost),
        overtimeHours: Math.round(totalOvertimeHours * 100) / 100,
        visitFee,
        hourlyRate,
        breakdown
    };
}

/**
 * Mark that the provider has reached the customer's work site.
 */
export async function reachedSite(bookingId, providerName) {
    try {
        const bookingRef = doc(db, 'bookings', bookingId);
        const bookingSnap = await getDoc(bookingRef);

        if (!bookingSnap.exists()) {
            throw new Error('Booking not found');
        }

        const currentData = bookingSnap.data();
        const statusHistory = currentData.statusHistory || [];

        await updateDoc(bookingRef, {
            status: 'reached-site',
            updatedAt: new Date().toISOString(),
            statusHistory: [
                ...statusHistory,
                {
                    status: 'reached-site',
                    timestamp: new Date().toISOString(),
                    note: `${providerName} reached the work site`
                }
            ]
        });

        console.log('✅ Provider reached site:', bookingId);
    } catch (error) {
        console.error('❌ Failed to update reached-site:', error);
        throw error;
    }
}

// ─── OTP-BASED ARRIVAL VERIFICATION ─────────────────────────────────────────

/**
 * Generate a 4-digit OTP for arrival verification.
 * The customer sees this OTP and shares it with the provider at their door.
 */
export async function generateArrivalOtp(bookingId) {
    try {
        const bookingRef = doc(db, 'bookings', bookingId);
        const bookingSnap = await getDoc(bookingRef);

        if (!bookingSnap.exists()) {
            throw new Error('Booking not found');
        }

        const otp = String(Math.floor(1000 + Math.random() * 9000)); // 4-digit

        await updateDoc(bookingRef, {
            arrivalOtp: otp,
            arrivalOtpGeneratedAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
        });

        console.log('✅ Arrival OTP generated for booking:', bookingId);
        return otp;
    } catch (error) {
        console.error('❌ Failed to generate arrival OTP:', error);
        throw error;
    }
}

/**
 * Verify arrival OTP and mark provider as reached-site.
 */
export async function verifyArrivalOtp(bookingId, enteredOtp, providerName) {
    try {
        const bookingRef = doc(db, 'bookings', bookingId);
        const bookingSnap = await getDoc(bookingRef);

        if (!bookingSnap.exists()) {
            throw new Error('Booking not found');
        }

        const currentData = bookingSnap.data();

        if (!currentData.arrivalOtp) {
            throw new Error('No arrival OTP has been generated for this booking');
        }

        if (currentData.arrivalOtp !== enteredOtp.trim()) {
            throw new Error('Incorrect OTP. Please ask the customer for the correct code.');
        }

        const statusHistory = currentData.statusHistory || [];

        await updateDoc(bookingRef, {
            status: 'reached-site',
            arrivalOtp: null,
            arrivalOtpVerified: true,
            arrivalOtpVerifiedAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            statusHistory: [
                ...statusHistory,
                {
                    status: 'reached-site',
                    timestamp: new Date().toISOString(),
                    note: `${providerName} reached site (verified by OTP)`
                }
            ]
        });

        console.log('✅ Arrival OTP verified & reached-site confirmed:', bookingId);
    } catch (error) {
        console.error('❌ Arrival OTP verification failed:', error);
        throw error;
    }
}

/**
 * Mark work as completed. Closes the last session, calculates final price.
 */
export async function workCompleted(bookingId, providerName, providerEmailOverride = '') {
    try {
        const bookingRef = doc(db, 'bookings', bookingId);
        const bookingSnap = await getDoc(bookingRef);

        if (!bookingSnap.exists()) {
            throw new Error('Booking not found');
        }

        const currentData = bookingSnap.data();
        const statusHistory = currentData.statusHistory || [];
        const workSessions = [...(currentData.workSessions || [])];
        const now = new Date().toISOString();

        // Close the last open session if any
        const lastSession = workSessions[workSessions.length - 1];
        if (lastSession && !lastSession.end) {
            lastSession.end = now;
        }

        const pricing = calculatePricing(workSessions, currentData.visitFee, currentData.hourlyRate);

        await updateDoc(bookingRef, {
            status: 'work-completed',
            workSessions,
            totalHoursWorked: pricing.totalHours,
            calculatedTotal: pricing.totalCost,
            finalPrice: pricing.totalCost,
            overtimeHours: pricing.overtimeHours,
            pricingBreakdown: pricing.breakdown,
            updatedAt: now,
            statusHistory: [
                ...statusHistory,
                {
                    status: 'work-completed',
                    timestamp: now,
                    note: `Work completed by ${providerName} (${pricing.totalHours.toFixed(1)} hrs, ₹${pricing.totalCost})`
                }
            ]
        });

        const customerEmail = await resolveCustomerEmail(currentData);
        if (customerEmail) {
            void triggerEmail(() => sendWorkCompleted(customerEmail, {
                customerName: currentData.customerName,
                serviceName: currentData.serviceName || currentData.serviceCategory || 'Service',
                bookingId,
                providerName,
                finalPrice: pricing.totalCost,
                calculatedTotal: pricing.totalCost,
                visitFee: currentData.visitFee || 0,
                hourlyRate: currentData.hourlyRate || 0,
                totalHoursWorked: pricing.totalHours,
                billableHours: pricing.billableHours,
                overtimeHours: pricing.overtimeHours,
                completedAt: now,
            }));
        } else {
            console.warn(`Skipping work completed email for ${bookingId}: no customer email found`);
        }

        const providerEmail = (providerEmailOverride || '').trim() || await resolveProviderEmail(currentData);
        if (providerEmail) {
            void triggerEmail(() => sendGenericNotification(providerEmail, {
                title: 'Work completion recorded',
                message: `Booking ${bookingId} has been marked as completed. Final amount: INR ${pricing.totalCost}.`,
            }));
        }

        console.log('✅ Work completed:', bookingId);
        return pricing;
    } catch (error) {
        console.error('❌ Failed to mark work-completed:', error);
        throw error;
    }
}

/**
 * Mark a booking as completed (provider finishes work).
 * @deprecated Use workCompleted() for the new status flow.
 */
export async function completeBooking(bookingId, providerName, finalPrice = null) {
    try {
        const bookingRef = doc(db, 'bookings', bookingId);
        const bookingSnap = await getDoc(bookingRef);

        if (!bookingSnap.exists()) {
            throw new Error('Booking not found');
        }

        const currentData = bookingSnap.data();
        const statusHistory = currentData.statusHistory || [];

        const updateData = {
            status: 'completed',
            updatedAt: new Date().toISOString(),
            completedAt: new Date().toISOString(),
            statusHistory: [
                ...statusHistory,
                {
                    status: 'completed',
                    timestamp: new Date().toISOString(),
                    note: `Work completed by ${providerName}`
                }
            ]
        };

        if (finalPrice !== null) {
            updateData.finalPrice = finalPrice;
        }

        await updateDoc(bookingRef, updateData);
        console.log('✅ Booking completed:', bookingId);
    } catch (error) {
        console.error('❌ Failed to complete booking:', error);
        throw error;
    }
}

const bookingService = {
    getProviderBookings,
    getAvailableBookings,
    getAllRelevantBookings,
    listenToProviderBookings,
    acceptBooking,
    rejectBooking,
    startWork,
    pauseWork,
    resumeWork,
    reachedSite,
    generateArrivalOtp,
    verifyArrivalOtp,
    workCompleted,
    completeBooking,
    calculatePricing
};

export default bookingService;
